import { Button } from '@/components/ui/button'
import { router } from '@inertiajs/react'
import { AlertCircle, FileText, LoaderCircle } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MemoryAddDialog from './memory-bank/MemoryAddDialog'
import MemoryAliveHeader from './memory-bank/MemoryAliveHeader'
import MemoryDetailDialog from './memory-bank/MemoryDetailDialog'
import MemoryDocumentDialog from './memory-bank/MemoryDocumentDialog'
import MemoryInspectorSidebar from './memory-bank/MemoryInspectorSidebar'
import MemoryNetworkView from './memory-bank/MemoryNetworkView'
import type { MemoryBankPayload, MemoryCopy, MemoryDetailPayload, MemoryDocumentSummary, MemoryGraphPayload, MemoryRow } from './memory-bank/types'
import { appendMemoryRows, displayDocumentName, isolateMemoryGraph, isolateMemoryGraphByDocuments, mergeMemoryGraphs } from './memory-bank/utils'

interface Props {
  workerId: string
  memoryBank?: MemoryBankPayload
  memoryDetail?: MemoryDetailPayload
  canManage: boolean
  copy: MemoryCopy
}

export default function MemoriaTab({ workerId, memoryBank, memoryDetail, canManage, copy }: Props) {
  const [baseMemory, setBaseMemory] = useState<MemoryBankPayload | undefined>(memoryBank)
  const [supplementalRows, setSupplementalRows] = useState<MemoryRow[]>([])
  const [documentFocusGraph, setDocumentFocusGraph] = useState<MemoryGraphPayload | null>(null)
  const [documentSearchReferences, setDocumentSearchReferences] = useState<string[] | null>(null)
  const [documentSearchGraph, setDocumentSearchGraph] = useState<MemoryGraphPayload | null>(null)
  const [focusLabel, setFocusLabel] = useState<string | null>(null)
  const [highlightedIds, setHighlightedIds] = useState<string[]>([])
  const [selectedMemory, setSelectedMemory] = useState<MemoryRow | null>(null)
  const [selectedDocument, setSelectedDocument] = useState<MemoryDocumentSummary | null>(null)
  const [addMode, setAddMode] = useState<'text' | 'files' | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [loading, setLoading] = useState(!memoryBank)
  const [refreshKey, setRefreshKey] = useState(0)
  const [filterResetKey, setFilterResetKey] = useState(0)
  const requestSequenceRef = useRef(0)
  const detailSequenceRef = useRef(0)
  const activeFocusRef = useRef('')
  const controlledRequestsRef = useRef(false)

  const load = useCallback(() => {
    controlledRequestsRef.current = true
    const requestSequence = requestSequenceRef.current + 1
    requestSequenceRef.current = requestSequence
    setLoading(true)
    router.reload({
      only: ['memory_bank'],
      preserveUrl: true,
      data: {
        memory_type: baseMemory?.fact_type || memoryBank?.fact_type || 'all',
        memory_document_id: '',
        memory_query: '',
        memory_limit: baseMemory?.graph.limit || memoryBank?.graph.limit || 500,
      },
      onSuccess: (page) => {
        if (requestSequenceRef.current !== requestSequence) return
        const nextMemory = page.props.memory_bank as MemoryBankPayload | undefined
        if (!nextMemory) return

        setBaseMemory(nextMemory)
        setDocumentFocusGraph(null)
        setHighlightedIds([])
      },
      onFinish: () => {
        if (requestSequenceRef.current === requestSequence) setLoading(false)
      },
    })
  }, [baseMemory?.fact_type, baseMemory?.graph.limit, memoryBank?.fact_type, memoryBank?.graph.limit])

  useEffect(() => {
    if (controlledRequestsRef.current || !memoryBank) return
    setBaseMemory(memoryBank)
    setLoading(false)
  }, [memoryBank])
  useEffect(() => {
    if (selectedMemory && memoryDetail?.memory?.id === selectedMemory.id) setDetailLoading(false)
  }, [memoryDetail, selectedMemory])

  const loadMemoryDetail = (row: MemoryRow) => {
    const requestSequence = detailSequenceRef.current + 1
    detailSequenceRef.current = requestSequence
    setDetailLoading(true)
    router.reload({
      only: ['memory_detail'],
      preserveUrl: true,
      data: { memory_id: row.id },
      onFinish: () => { if (detailSequenceRef.current === requestSequence) setDetailLoading(false) },
    })
  }

  const clearFocus = (resetRecall = true) => {
    requestSequenceRef.current += 1
    activeFocusRef.current = ''
    setFocusLabel(null)
    setSupplementalRows([])
    setDocumentFocusGraph(null)
    setDocumentSearchReferences(null)
    setDocumentSearchGraph(null)
    setHighlightedIds([])
    setSelectedMemory(null)
    setLoading(false)
    if (resetRecall) setFilterResetKey((value) => value + 1)
  }

  const openMemory = (row: MemoryRow) => {
    setSelectedMemory(row)
    loadMemoryDetail(row)
  }

  const openMemorySource = (documentId: string) => {
    setSelectedMemory(null)
    setSelectedDocument({
      id: documentId,
      name: copy.detail.source,
      category: 'manual',
      text_length: 0,
      memory_unit_count: 0,
    })
  }

  const previewMemory = (row: MemoryRow) => {
    const key = `memory:${row.id}`
    if (activeFocusRef.current === key) return
    setDocumentFocusGraph(null)
    const isVisible = Boolean(
      baseMemory?.graph.nodes.some((node) => node.data.id === row.id) ||
      supplementalRows.some((memory) => memory.id === row.id),
    )
    if (isVisible) {
      activeFocusRef.current = key
      setFocusLabel(row.text.slice(0, 56))
      setHighlightedIds([row.id])
      return
    }

    activeFocusRef.current = key
    setFocusLabel(row.text.slice(0, 56))
    setSupplementalRows((current) => current.some((memory) => memory.id === row.id) ? current : [...current, row])
    setHighlightedIds([row.id])
  }

  const previewDocument = (document: MemoryDocumentSummary, focusedGraph?: MemoryGraphPayload, isolate = false) => {
    const visibleRows = Array.from(new Map(
      [
        ...(baseMemory?.graph.table_rows || []),
        ...(documentSearchGraph?.table_rows || []),
      ]
        .filter((row) => row.document_id === document.id)
        .map((row) => [row.id, row]),
    ).values())
    if (!isolate) {
      const ids = visibleRows.map((row) => row.id)
      const key = `document-preview:${document.id}:${ids.length}`
      if (activeFocusRef.current === key) return

      activeFocusRef.current = key
      setFocusLabel(displayDocumentName(document.name))
      setDocumentFocusGraph(null)
      setSupplementalRows([])
      setHighlightedIds(ids)
      setSelectedMemory(null)
      return
    }

    const graph = focusedGraph || (baseMemory && visibleRows.length > 0
      ? isolateMemoryGraph(baseMemory.graph, visibleRows, document.memory_unit_count)
      : null)
    const firstNode = graph?.nodes.at(0)?.data.id || ''
    const lastNode = graph?.nodes.at(-1)?.data.id || ''
    const key = `document:${document.id}:${graph?.nodes.length || 0}:${graph?.edges.length || 0}:${firstNode}:${lastNode}`
    if (activeFocusRef.current === key) return

    activeFocusRef.current = key
    setFocusLabel(displayDocumentName(document.name))
    setSupplementalRows([])
    setHighlightedIds([])
    setSelectedMemory(null)
    setDocumentFocusGraph(graph)
  }

  const filterDocumentSearch = useCallback((documents: MemoryDocumentSummary[], query: string, graph?: MemoryGraphPayload) => {
    const normalizedQuery = query.trim()
    if (!normalizedQuery) {
      setDocumentSearchReferences(null)
      setDocumentSearchGraph(null)
      if (activeFocusRef.current.startsWith('document')) {
        activeFocusRef.current = ''
        setFocusLabel(null)
        setSupplementalRows([])
        setDocumentFocusGraph(null)
        setHighlightedIds([])
        setSelectedMemory(null)
      }
      return
    }

    activeFocusRef.current = `documents-search:${normalizedQuery}`
    setDocumentSearchReferences(documents.map((document) => document.id))
    setDocumentSearchGraph(graph || null)
    setDocumentFocusGraph(null)
    setSupplementalRows([])
    setHighlightedIds([])
    setSelectedMemory(null)
    setFocusLabel(normalizedQuery)
  }, [])

  const prepareRecall = (query: string) => {
    requestSequenceRef.current += 1
    activeFocusRef.current = `recall:${query}`
    setFocusLabel(null)
    setSupplementalRows([])
    setDocumentFocusGraph(null)
    setDocumentSearchReferences(null)
    setDocumentSearchGraph(null)
    setHighlightedIds([])
    setLoading(false)
  }

  const refreshBaseGraph = () => {
    clearFocus(true)
    setRefreshKey((value) => value + 1)
    load()
  }

  const currentMemory = useMemo(() => {
    if (!baseMemory) return memoryBank
    if (baseMemory.status !== 'ready') return baseMemory
    const augmentedGraph = appendMemoryRows(baseMemory.graph, supplementalRows)
    const searchableGraph = documentSearchGraph
      ? mergeMemoryGraphs(augmentedGraph, documentSearchGraph)
      : augmentedGraph
    const searchedGraph = documentSearchReferences
      ? isolateMemoryGraphByDocuments(searchableGraph, documentSearchReferences)
      : searchableGraph
    return { ...baseMemory, graph: documentFocusGraph || searchedGraph }
  }, [baseMemory, documentFocusGraph, documentSearchGraph, documentSearchReferences, memoryBank, supplementalRows])
  const graph = currentMemory?.graph
  const status = currentMemory?.status

  return (
      <section data-testid="memory-alive" data-memory-type={currentMemory?.fact_type} className="memory-alive-shell relative grid w-full overflow-hidden rounded-xl border border-border bg-card shadow-xs lg:h-full lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_23rem] lg:grid-rows-[4rem_minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_25rem] 2xl:grid-cols-[minmax(0,1fr)_30rem]">
      <MemoryAliveHeader copy={copy} total={baseMemory?.stats?.total_memories ?? baseMemory?.graph.total_units ?? graph?.total_units ?? 0} visibleCount={baseMemory?.graph.table_rows.length || graph?.table_rows.length || 0} truncated={baseMemory?.graph.truncated || graph?.truncated || false} loading={loading} stats={baseMemory?.stats || currentMemory?.stats} />

      <div className="relative min-w-0 lg:col-start-1 lg:row-start-2 lg:h-full lg:min-h-0">
        {(!currentMemory || loading && !graph) && <LoadingState copy={copy} />}
        {status === 'unavailable' && <StatusCard icon={AlertCircle} title={copy.unavailable.title} description={copy.unavailable.description} />}
        {status === 'error' && <StatusCard icon={AlertCircle} title={copy.error.title} description={copy.error.description} action={copy.error.retry} onAction={() => load()} />}
        {status === 'ready' && graph?.total_units === 0 && <StatusCard icon={FileText} title={copy.empty.title} description={copy.empty.description} />}
        {status === 'ready' && graph && graph.total_units > 0 && <MemoryNetworkView copy={copy} graph={graph} selected={selectedMemory} highlightedIds={highlightedIds} focusLabel={focusLabel} onSelect={openMemory} onClearFocus={() => clearFocus(true)} />}
      </div>

      <MemoryInspectorSidebar workerId={workerId} copy={copy} canManage={canManage} refreshKey={refreshKey} filterResetKey={filterResetKey} onSelectMemory={openMemory} onSelectDocument={setSelectedDocument} onPreviewMemory={previewMemory} onPreviewDocument={previewDocument} onDocumentSearchResults={filterDocumentSearch} onRecallSearch={prepareRecall} onClearRecall={() => clearFocus(false)} onAddDocument={() => setAddMode('files')} />

      {addMode && <MemoryAddDialog workerId={workerId} copy={copy} initialMode={addMode} onSubmitted={() => setRefreshKey((value) => value + 1)} onClose={() => setAddMode(null)} />}
      {selectedMemory && <MemoryDetailDialog workerId={workerId} copy={copy} row={selectedMemory} detail={memoryDetail} loading={detailLoading} canManage={canManage} onMutated={refreshBaseGraph} onSelectSource={openMemorySource} onClose={() => setSelectedMemory(null)} />}
      {selectedDocument && <MemoryDocumentDialog workerId={workerId} copy={copy} summary={selectedDocument} canManage={canManage} onSelectMemory={(row) => { setSelectedDocument(null); openMemory(row) }} onDeleted={() => { setSelectedDocument(null); refreshBaseGraph() }} onClose={() => setSelectedDocument(null)} />}
    </section>
  )
}

function LoadingState({ copy }: { copy: MemoryCopy }) {
  return <div className="flex h-[560px] flex-col items-center justify-center gap-3 bg-background lg:h-full"><LoaderCircle className="h-7 w-7 animate-spin text-primary" /><p className="text-sm text-muted-foreground">{copy.status.loading}</p></div>
}

function StatusCard({ icon: Icon, title, description, action, onAction }: { icon: typeof AlertCircle; title: string; description: string; action?: string; onAction?: () => void }) {
  return <div className="flex h-[560px] flex-col items-center justify-center gap-3 bg-background p-8 text-center lg:h-full"><Icon className="h-9 w-9 text-muted-foreground/35" /><h3 className="text-base font-semibold text-foreground">{title}</h3><p className="max-w-md text-sm leading-6 text-muted-foreground">{description}</p>{action && onAction && <Button type="button" onClick={onAction} className="mt-2">{action}</Button>}</div>
}
